"use server";

import { after } from "next/server";
import { notifyLead } from "./telegram";
import { leadFormSchema, type LeadFormValues } from "../model/schema";
import type { LeadField, LeadFormState } from "../model/types";

const LEAD_FIELDS: readonly string[] = [
  "name",
  "phone",
  "email",
  "objectType",
  "message",
  "consent",
];

/** Server-only write after Zod validation. Public REST creation is disabled. */
async function persistLead(values: LeadFormValues): Promise<void> {
  const [{ getPayload }, { default: config }] = await Promise.all([
    import("payload"), import("@payload-config"),
  ]);
  const payload = await getPayload({ config });
  await payload.create({ collection: "leads", overrideAccess: true, data: { ...values, status: "new" } });
}

export async function submitLead(
  _prev: LeadFormState,
  formData: FormData,
): Promise<LeadFormState> {
  // consent приходит строкой «on» — превращаем в булево до валидации.
  const raw = {
    name: String(formData.get("name") ?? ""),
    phone: String(formData.get("phone") ?? ""),
    email: String(formData.get("email") ?? ""),
    objectType: String(formData.get("objectType") ?? ""),
    message: String(formData.get("message") ?? ""),
    consent: formData.get("consent") === "on",
  };

  const parsed = leadFormSchema.safeParse(raw);

  if (!parsed.success) {
    const fieldErrors: LeadFormState["fieldErrors"] = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0];
      if (
        typeof key === "string" &&
        (LEAD_FIELDS as readonly string[]).includes(key)
      ) {
        const field = key as LeadField;
        fieldErrors[field] ??= issue.message;
      }
    }
    return {
      status: "error",
      message: "Проверьте выделенные поля",
      fieldErrors,
    };
  }

  try {
    await persistLead(parsed.data);
  } catch {
    return { status: "error", message: "Не удалось сохранить заявку. Попробуйте ещё раз или свяжитесь с нами напрямую." };
  }

  after(() => notifyLead(parsed.data));

  return {
    status: "success",
    message: "Заявка отправлена. Перезвоним в течение рабочего дня.",
  };
}
